import { Suspense, useEffect, useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, ContactShadows, Environment, Lightformer } from "@react-three/drei";
import Headphones from "./Headphones";
import { Field, GlowRings } from "./Field";

function Rig({ children }) {
    const ref = useRef(null);
    useFrame((state, delta) => {
        const g = ref.current;
        if (!g) return;
        const k = 1 - Math.exp(-delta * 3);
        g.rotation.y += (state.pointer.x * 0.12 - g.rotation.y) * k;
        g.rotation.x += (-state.pointer.y * 0.08 - g.rotation.x) * k;
    });
    return <group ref={ref}>{children}</group>;
}

function SceneContent({ variant, spinKey }) {
    const controls = useRef(null);
    const resume = useRef(null);

    useEffect(() => {
        if (controls.current) controls.current.target.set(0, 0, 0);
    }, []);

    return (
        <>
            <ambientLight intensity={0.5} />
            <directionalLight position={[-4, 4, 3]} intensity={1.6} color="#9BE9FF" />
            <pointLight position={[3, -2, 2]} intensity={30} color="#7000FF" />
            <pointLight position={[-3, 1.5, -2]} intensity={20} color="#00F0FF" />

            <Environment resolution={256}>
                <Lightformer intensity={2.2} position={[0, 4, 2]} scale={[8, 3, 1]} color="#CFE9FF" />
                <Lightformer intensity={1.6} position={[-4, 0, 1]} rotation-y={Math.PI / 2} scale={[6, 2, 1]} color="#00F0FF" />
                <Lightformer intensity={1.6} position={[4, -1, 1]} rotation-y={-Math.PI / 2} scale={[6, 2, 1]} color="#7000FF" />
                <Lightformer intensity={1.1} position={[0, 0, -4]} scale={[6, 6, 1]} color="#3B2A66" />
            </Environment>

            <Rig>
                <Headphones variant={variant} spinKey={spinKey} />
            </Rig>

            <Field count={320} color="#00F0FF" size={0.02} radius={[2.4, 4.6]} speed={0.02} opacity={0.5} />
            <Field count={140} color="#A855F7" size={0.03} radius={[3, 5]} speed={-0.014} opacity={0.35} />
            <GlowRings />

            <ContactShadows
                position={[0, -1.55, 0]}
                opacity={0.6}
                scale={9}
                blur={2.8}
                far={3}
                color="#010208"
            />

            <OrbitControls
                ref={controls}
                makeDefault
                enableZoom={false}
                enablePan={false}
                enableDamping
                dampingFactor={0.08}
                rotateSpeed={0.85}
                autoRotate
                autoRotateSpeed={0.7}
                onStart={() => {
                    clearTimeout(resume.current);
                    if (controls.current) controls.current.autoRotate = false;
                }}
                onEnd={() => {
                    resume.current = setTimeout(() => {
                        if (controls.current) controls.current.autoRotate = true;
                    }, 2600);
                }}
            />
        </>
    );
}

export default function HeroScene({ variant, spinKey }) {
    return (
        <Canvas
            dpr={[1, 1.75]}
            camera={{ position: [0, 0.15, 4.6], fov: 38 }}
            gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
            style={{ background: "transparent" }}
        >
            <Suspense fallback={null}>
                <SceneContent variant={variant} spinKey={spinKey} />
            </Suspense>
        </Canvas>
    );
}
